import PageHeaderAction from '../../../components/PageHeaderAction';
import ActionsMenu from '../../../components/ui/ActionsMenu';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import { useCreateData, useFetchData, useFetchFile } from '../../../hooks';
import {
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  FileSyncOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import PurchaseOrderInfoContent from './PurchaseOrderInfoContent';
import AuditCard from '../../../components/AuditCard';
import TrackingChain from '../../../components/TrackingChain';
import PurchaseOrderInfoStatus from './PurchaseOrderInfoStatus';
import PurchaseOrderInfoViewSkeleton from './PurchaseOrderInfoViewSkeleton';
import PurchaseOrderPrintTemplate from './PurchaseOrderPrintTemplate';
import { useReactToPrint } from 'react-to-print';
import InfoModal from '../../../components/ui/modal/InfoModal';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { PurchaseOrderInfo } from '../../../types/PurchaseOrder';
import { StatusHistory } from '../../../types';
import { isUserCanEdit } from '../../../lib/utils';
import { useAuthStore } from '../../../store';

const PurchaseOrderInfoPage = () => {
  const api = '/purchase/orders';
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const componentRef = useRef<HTMLDivElement>(null);

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const { data: purchaseOrderInfo, isLoading } =
    useFetchData<PurchaseOrderInfo>(`${api}/${params.id}`);

  const { data: files } = useFetchFile(
    `${api}/${params.id}/files`,
    'api/purchase/orders',
  );

  const { data: statusHistory, isLoading: isLoadingHistory } = useFetchData<
    StatusHistory[]
  >(`purchase/orders/${params.id}/status-logs`);

  const {
    mutate: mutateGen,
    isSuccess: isSuccessGen,
    data: rrData,
  } = useCreateData(`/stock/rrs/generateRR/${params.id}`);
  const handleGenerateRR = () => {
    mutateGen(params.id);
  };

  useEffect(() => {
    if (isSuccessGen) setIsInfoModalOpen(true);
  }, [isSuccessGen]);
  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const exportToPDF = () => {
    html2canvas(componentRef.current as HTMLDivElement).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const componentWidth = pdf.internal.pageSize.getWidth();
      const componentHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);
      pdf.save(`purchase_order_${purchaseOrderInfo?.code}.pdf`);
    });
  };

  const isCurrentUserCanEdit = () => {
    return (
      user?.role === 'ROLE_ADMIN' ||
      isUserCanEdit(user?.moduleAccess, 'PurchaseOrder')
    );
  };

  const items: MenuProps['items'] = [
    {
      label: 'Edit',
      key: 'edit',
      icon: <EditOutlined />,
      disabled:
        !isCurrentUserCanEdit() ||
        purchaseOrderInfo?.status === 'APPROVED' ||
        purchaseOrderInfo?.status === 'CANCEL',
    },
    {
      label: 'Generate RR',
      key: 'gen_rr',
      icon: <FileSyncOutlined />,
      disabled: purchaseOrderInfo?.status !== 'APPROVED',
      onClick: handleGenerateRR,
    },
    {
      label: 'Download',
      key: 'download',
      icon: <DownloadOutlined />,
      onClick: exportToPDF,
    },
    {
      label: 'Print',
      key: 'print',
      icon: <PrinterOutlined />,
      onClick: handlePrint, // Trigger print action directly when clicked
    },
  ];

  const onClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'edit') {
      navigate(`/purchase-order/request/${purchaseOrderInfo?.id}`);
    }
  };

  const handleClose = () => {
    navigate('/purchase-order');
  };

  if (isLoading && isLoadingHistory) {
    return <PurchaseOrderInfoViewSkeleton active={true} />;
  }

  const handleInfoModalNavigate = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/receiving-report/' + rrData?.data.id);
  };

  return (
    <>
      {purchaseOrderInfo !== null && purchaseOrderInfo !== undefined && (
        <>
          <div className={'flex items-center justify-between'}>
            <PageHeaderAction
              moduleName={'Purchase Order'}
              action={searchParams.get('action')}
            />
            <ActionsMenu items={items} onClick={onClick} onBack={handleClose} />
          </div>
          <div className={'flex'}>
            <PurchaseOrderInfoContent
              data={purchaseOrderInfo}
              attachments={files}
              api={api}
              recordId={params.id}
              canEdit={isUserCanEdit(user?.moduleAccess, 'PurchaseOrder')}
            />
            <div className={'w-3/12'}>
              <PurchaseOrderInfoStatus
                data={purchaseOrderInfo}
                statusHistory={statusHistory !== undefined ? statusHistory : []}
              />
              <div className={'m-4 space-y-4'}>
                <TrackingChain api={api} id={params.id} />
                <AuditCard
                  createdBy={purchaseOrderInfo?.createdBy}
                  updatedBy={purchaseOrderInfo?.updatedBy}
                  timeCreated={purchaseOrderInfo?.timeCreated}
                  timeUpdated={purchaseOrderInfo?.timeUpdated}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <InfoModal
        visible={isInfoModalOpen}
        message={
          'Successfully generated RR\nDo you want to view Receiving Report?'
        }
        onClose={handleInfoModalClose}
        onOk={handleInfoModalNavigate}
      />

      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <PurchaseOrderPrintTemplate
          data={purchaseOrderInfo}
          ref={componentRef}
        />
      </div>
    </>
  );
};
export default PurchaseOrderInfoPage;
